/**
 * LinkedIn Insight Tag integration
 *
 * Enables LinkedIn retargeting and conversion tracking.
 * Uses script tag injection (same pattern as Clarity).
 */

import { logWarn, logInfo } from '@/lib/logger';

interface LinkedInWindow extends Window {
  _linkedin_data_partner_ids?: string[];
  lintrk?: (action: string, data: Record<string, unknown>) => void;
}

let isInitialized = false;

/**
 * Initialize LinkedIn Insight Tag via script tag injection
 */
export function init(partnerId: string): void {
  if (isInitialized) return;
  if (typeof window === 'undefined') return;

  if (!partnerId) {
    logWarn('[LinkedIn] No partner ID provided, skipping initialization');
    return;
  }

  try {
    const w = window as LinkedInWindow;
    w._linkedin_data_partner_ids = w._linkedin_data_partner_ids || [];
    w._linkedin_data_partner_ids.push(partnerId);

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);

    // Add noscript pixel fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.alt = '';
    img.src = `https://px.ads.linkedin.com/collect/?pid=${partnerId}&fmt=gif`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    isInitialized = true;
    logInfo('[LinkedIn] Insight Tag initialized with partner ID:', partnerId);
  } catch (error) {
    logWarn('[LinkedIn] Failed to initialize:', error);
  }
}

/**
 * Track a LinkedIn conversion event
 * Use after setting up conversion actions in LinkedIn Campaign Manager
 */
export function trackConversion(conversionId: string): void {
  if (!isInitialized) return;

  try {
    const w = window as LinkedInWindow;
    if (typeof w.lintrk === 'function') {
      w.lintrk('track', { conversion_id: conversionId });
    }
  } catch (error) {
    logWarn('[LinkedIn] Conversion tracking failed:', error);
  }
}
